import { compareRevisions } from '../lib/revision-order.js';
import { folderPath } from './location-navigation.js';

/** 一次最多畫出的列數；全選與批次操作仍涵蓋所有符合搜尋的項目。 */
const RENDER_LIMIT = 500;

const fmtDateTime = (iso) => {
  const d = new Date(iso || '');
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * 設定 → 封存匣：列出軟刪除的項目，可單筆／批次還原或永久刪除。
 * deps: { getTree() → {projects, folders}, fmtBytes, escapeHtml, toast, onChanged }
 */
export class ArchivePanel {
  constructor(root, deps) {
    this.root = root;
    this.deps = deps;
    this.items = [];
    this.bytes = 0;
    this.selected = new Set();
    this.query = '';
    this.busy = false;
    this.$ = (sel) => root.querySelector(sel);
    this.$('#archiveSearch').addEventListener('input', (e) => { this.query = e.target.value.trim().toLowerCase(); this.render(); });
    this.$('#archiveRefresh').addEventListener('click', () => this.load());
    this.$('#archiveRestore').addEventListener('click', () => this.restore([...this.selected]));
    this.$('#archivePurge').addEventListener('click', () => this.purge([...this.selected]));
    this.$('#archivePurgeAll').addEventListener('click', () => this.purgeAll());
  }

  async load() {
    try {
      const data = await (await fetch('/api/library/archive', { cache: 'no-store' })).json();
      if (!data.ok) throw new Error(data.error || '讀取失敗');
      const groups = new Map();
      for (const item of data.items) {
        const key = item.documentId || item.id;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      }
      this.items = [...groups.values()].map(versions => {
        versions.sort(compareRevisions);
        return { ...versions[0], versions,
          diskBytes: versions.reduce((sum, it) => sum + it.diskBytes, 0),
          deletedAt: versions.map(it => it.deletedAt).sort().at(-1) };
      }).sort((a, b) => b.deletedAt.localeCompare(a.deletedAt));
      this.bytes = data.bytes;
      const ids = new Set(this.items.map((it) => it.id));
      for (const id of this.selected) if (!ids.has(id)) this.selected.delete(id);
    } catch (e) {
      this.items = [];
      this.bytes = 0;
      this.$('#archiveList').innerHTML = `<p class="empty">封存匣讀取失敗：${this.deps.escapeHtml(e.message)}</p>`;
      return;
    }
    this.render();
  }

  filtered() {
    if (!this.query) return this.items;
    return this.items.filter((it) => [it.name, it.meta?.drawingNo, it.meta?.title, ...it.versions.map(v => v.meta?.revision)]
      .some((v) => v && v.toLowerCase().includes(this.query)));
  }

  /** 與伺服器還原規則一致：原專案／目錄已不存在時會退回未分類／專案根。 */
  whereOf(item) {
    const { projects, folders } = this.deps.getTree();
    if (!item.projectId) return { text: '未分類', note: '' };
    const project = projects.find((p) => p.id === item.projectId);
    if (!project) return { text: '未分類', note: '原專案已刪除，將還原到未分類' };
    if (item.folderId && !folders.some((f) => f.id === item.folderId)) {
      return { text: project.name, note: '原目錄已刪除，將還原到專案根目錄' };
    }
    const folder = item.folderId ? folderPath(folders, item.folderId) : '';
    return { text: [project.name, folder].filter(Boolean).join(' / '), note: '' };
  }

  render() {
    const { fmtBytes, escapeHtml } = this.deps;
    const rows = this.filtered();
    const selectedBytes = this.items.filter((it) => this.selected.has(it.id)).reduce((s, it) => s + it.diskBytes, 0);
    this.$('#archiveSummary').textContent = this.items.length ? `${this.items.length} 份文件 · 約 ${fmtBytes(this.bytes)}` : '空';
    this.$('#archiveSelCount').textContent = this.selected.size ? `已選 ${this.selected.size} 份文件（約 ${fmtBytes(selectedBytes)}）` : '';
    this.$('#archiveRestore').disabled = this.busy || !this.selected.size;
    this.$('#archivePurge').disabled = this.busy || !this.selected.size;
    this.$('#archivePurgeAll').disabled = this.busy || !this.items.length;
    this.$('#archiveSearch').disabled = !this.items.length;
    const list = this.$('#archiveList');
    if (!this.items.length) { list.innerHTML = '<p class="empty">封存匣是空的。</p>'; return; }
    if (!rows.length) { list.innerHTML = '<p class="empty">沒有符合搜尋的封存項目。</p>'; return; }

    const table = document.createElement('table');
    table.className = 'file-table archive-table';
    const allOn = rows.every((it) => this.selected.has(it.id));
    table.innerHTML = `<thead><tr>
      <th scope="col" class="col-check"><input type="checkbox" data-act="all" aria-label="全選符合搜尋的封存項目" ${allOn ? 'checked' : ''}></th>
      <th scope="col">檔案</th><th scope="col">歷程版次（新→舊）</th><th scope="col">原位置</th><th scope="col">封存時間</th><th scope="col">佔用</th><th scope="col">操作</th>
    </tr></thead><tbody></tbody>`;
    const body = table.querySelector('tbody');
    for (const item of rows.slice(0, RENDER_LIMIT)) {
      const where = this.whereOf(item);
      const title = item.meta?.drawingNo ? `${item.meta.drawingNo} ${item.meta.title || ''}`.trim() : '';
      const row = document.createElement('tr');
      row.dataset.id = item.id;
      row.innerHTML = `
        <td class="col-check"><input type="checkbox" data-act="pick" aria-label="選取 ${escapeHtml(item.name)} 版次 ${escapeHtml(item.meta?.revision || '未標版次')}" ${this.selected.has(item.id) ? 'checked' : ''}></td>
        <td><div class="fr-name" title="${escapeHtml(item.name)}">${escapeHtml(item.name)}</div>${title ? `<div class="fr-file">${escapeHtml(title)}</div>` : ''}</td>
        <td class="mono">${item.versions.map(v => escapeHtml(v.meta?.revision || '未標版次')).join('、')}</td>
        <td title="${escapeHtml(where.note || where.text)}">${escapeHtml(where.text)}${where.note ? `<div class="archive-note">${escapeHtml(where.note)}</div>` : ''}</td>
        <td class="mono">${fmtDateTime(item.deletedAt)}</td>
        <td class="mono">${fmtBytes(item.diskBytes)}</td>
        <td class="col-actions">
          <button type="button" class="btn btn-sm" data-act="restore" ${this.busy ? 'disabled' : ''}>還原整份文件</button>
          <button type="button" class="btn btn-sm btn-danger" data-act="purge" ${this.busy ? 'disabled' : ''}>永久刪除</button>
        </td>`;
      body.appendChild(row);
    }
    table.addEventListener('change', (e) => {
      const act = e.target.dataset.act;
      if (act === 'all') for (const it of rows) e.target.checked ? this.selected.add(it.id) : this.selected.delete(it.id);
      else if (act === 'pick') {
        const id = e.target.closest('tr').dataset.id;
        e.target.checked ? this.selected.add(id) : this.selected.delete(id);
      } else return;
      this.render();
    });
    table.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-act]');
      if (!btn) return;
      const id = btn.closest('tr').dataset.id;
      if (btn.dataset.act === 'restore') this.restore([id]);
      else if (btn.dataset.act === 'purge') this.purge([id]);
    });
    const parts = [table];
    if (rows.length > RENDER_LIMIT) {
      const more = document.createElement('p');
      more.className = 'side-help';
      more.textContent = `僅顯示最近 ${RENDER_LIMIT} 筆（共 ${rows.length} 筆符合）；全選與批次操作涵蓋全部符合項目，可用搜尋縮小範圍。`;
      parts.push(more);
    }
    list.replaceChildren(...parts);
  }

  async post(action, body) {
    this.busy = true;
    this.render();
    try {
      const res = await fetch(`/api/library/archive/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    } finally {
      this.busy = false;
    }
  }

  async restore(ids) {
    if (!ids.length) return;
    try {
      const r = await this.post('restore', { ids });
      for (const id of ids) this.selected.delete(id);
      this.deps.toast(`已還原 ${r.restored} 個版次（整份文件）${r.relocated ? `（其中 ${r.relocated} 項原目錄已刪除，已放到專案根目錄或未分類）` : ''}`);
      await this.deps.onChanged();
    } catch (e) {
      this.deps.toast(`還原失敗：${e.message}`);
    }
    await this.load();
  }

  async purge(ids) {
    if (!ids.length) return;
    const picked = this.items.filter((it) => ids.includes(it.id));
    const bytes = picked.reduce((s, it) => s + it.diskBytes, 0);
    const label = picked.length === 1 ? `「${picked[0].name}」（含 ${picked[0].versions.length} 個封存版次）` : `${picked.length} 份文件（含所有封存版次）`;
    if (!confirm(`永久刪除${label}？\n將刪除原始檔、轉檔結果、標註與書籤（約 ${this.deps.fmtBytes(bytes)}），無法復原。`)) return;
    await this.runPurge({ ids: picked.flatMap(it => it.versions.map(v => v.id)) }, ids);
  }

  async purgeAll() {
    if (!this.items.length) return;
    if (!confirm(`清空封存匣？\n將永久刪除全部 ${this.items.length} 份文件的所有封存版次（約 ${this.deps.fmtBytes(this.bytes)}），無法復原。現行檔案不受影響。`)) return;
    await this.runPurge({ all: true }, this.items.map((it) => it.id));
  }

  async runPurge(body, ids) {
    try {
      const r = await this.post('purge', body);
      for (const id of ids) this.selected.delete(id);
      this.deps.toast(`已永久刪除 ${r.purged} 項`);
      await this.deps.onChanged();
    } catch (e) {
      this.deps.toast(`永久刪除失敗：${e.message}`);
    }
    await this.load();
  }
}
